/**
 * Order confirmation card — the one manual checkpoint left in the chat
 * ordering flow. Quantity, delivery address, and payment method are all
 * gathered conversationally by the AI beforehand; this card just recaps
 * what it collected and asks for a single tap to place the real order.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCountry } from "@/lib/country-context";
import api from "@/lib/api";

export function OrderConfirmCard({ draft, sessionId, onPlaced, onCancel, busy }) {
  const { formatPrice } = useCountry();
  const navigate = useNavigate();
  const [placing, setPlacing] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [order, setOrder] = useState(null);

  const confirm = async () => {
    setPlacing(true);
    setErrorMsg("");
    try {
      const { data } = await api.post("/orders", {
        restaurant_id: draft.restaurant_id,
        delivery_address: draft.delivery_address,
        delivery_latitude: draft.delivery_latitude,
        delivery_longitude: draft.delivery_longitude,
        notes: draft.notes || null,
        payment_method: draft.payment_method,
        session_id: sessionId,
        items: [{ menu_item_id: draft.menu_item_id, quantity: draft.quantity }],
      });
      setOrder(data);
      onPlaced?.(data);
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || "Couldn't place your order. Try again.");
    } finally {
      setPlacing(false);
    }
  };

  if (order) {
    return (
      <div className="bg-card border rounded-2xl p-4 shadow-elegant max-w-md" data-testid="order-confirm-done">
        <div className="flex items-center gap-2 pb-2">
          <CheckCircle2 className="w-5 h-5 text-primary" />
          <p className="font-semibold text-sm">Order placed!</p>
        </div>
        <div className="bg-muted/40 rounded-xl p-3 text-sm space-y-1">
          <div className="flex justify-between"><span className="text-muted-foreground">Order ID</span>
            <span className="font-mono font-bold">#{order.id.slice(0, 8).toUpperCase()}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Total</span>
            <span className="font-bold">{formatPrice(order.total)}</span></div>
          {order.estimated_delivery && (
            <div className="flex justify-between"><span className="text-muted-foreground">ETA</span>
              <span><Truck className="w-3 h-3 inline mr-1" />
                {new Date(order.estimated_delivery).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
              </span></div>
          )}
        </div>
        <Button className="w-full mt-3 bg-gradient-primary text-primary-foreground shadow-glow"
          onClick={() => navigate(`/orders/${order.id}`)} data-testid="order-confirm-track">
          Track order
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-card border rounded-2xl p-4 shadow-elegant max-w-md" data-testid="order-confirm-card">
      <p className="text-[10px] font-bold uppercase tracking-widest text-primary pb-2">Ready to order</p>
      <div className="bg-muted/40 rounded-xl p-3 text-sm space-y-1.5">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Item</span>
          <span className="font-semibold">{draft.quantity}× {draft.item_name}</span>
        </div>
        <div className="flex justify-between gap-3">
          <span className="text-muted-foreground shrink-0">To</span>
          <span className="text-right truncate">{draft.delivery_address}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Payment</span>
          <span className="capitalize">{draft.payment_method?.replace(/_/g, " ")}</span>
        </div>
        {draft.notes && (
          <div className="flex justify-between gap-3">
            <span className="text-muted-foreground shrink-0">Notes</span>
            <span className="text-right truncate">{draft.notes}</span>
          </div>
        )}
        <div className="flex justify-between pt-2 border-t font-bold">
          <span>Estimated total</span>
          <span>{formatPrice(draft.subtotal)} <span className="text-[10px] font-normal text-muted-foreground">+ delivery</span></span>
        </div>
      </div>

      {errorMsg && <p className="text-xs text-destructive mt-2">{errorMsg}</p>}

      <Button className="w-full mt-3 bg-gradient-primary text-primary-foreground shadow-glow font-semibold gap-2"
        onClick={confirm} disabled={placing || busy} data-testid="order-confirm-btn">
        {placing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm order"}
      </Button>
      <button onClick={onCancel} disabled={placing || busy}
        className="text-xs text-muted-foreground hover:text-foreground w-full text-center pt-2 disabled:opacity-50"
        data-testid="order-confirm-cancel">
        Cancel
      </button>
    </div>
  );
}
